import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Award, Shield } from "lucide-react";
import { FooterLinks } from "@/components/InternalLinks";
import { useLanguage } from "@/contexts/LanguageContext";

interface SiteFooterProps {
  /** Compact variant: skip the four-column FooterLinks grid for pages where the
   * full sitemap block is too heavy (e.g. policy pages, admin-adjacent flows). */
  compact?: boolean;
}

/**
 * Site-wide footer used by every public page. Centralizes:
 *   - Brand + ISO/GMP badges
 *   - The four-column FooterLinks block (sitemap, services, company, support)
 *   - Copyright with live year (no hardcoded 2024 leaks)
 *   - Privacy / Return / Contact policy links
 */
export const SiteFooter = ({ compact = false }: SiteFooterProps) => {
  const { t } = useLanguage();

  return (
    <footer className="bg-foreground text-background py-12">
      <div className="container mx-auto px-4">
        {!compact && (
          <>
            <div className="mb-10">
              <div className="flex items-center mb-4">
                <div className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-bold text-xl mr-4">
                  Longshine
                </div>
                <div>
                  <div className="font-semibold text-lg">{t("footer.company") || "Longshine Boi-Tech Pvt. Ltd."}</div>
                  <div className="text-sm text-muted">{t("footer.certified") || "ISO Certified Excellence"}</div>
                </div>
              </div>
              <p className="text-muted mb-6 max-w-2xl">
                {t("footer.description") ||
                  "Premium pharmaceutical APIs, intermediates, and veterinary compounds — manufactured to international standards with full regulatory support."}
              </p>
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-background border-background">
                  <Award className="w-3 h-3 mr-1" />
                  ISO 9001:2015
                </Badge>
                <Badge variant="outline" className="text-background border-background">
                  <Shield className="w-3 h-3 mr-1" />
                  GMP Certified
                </Badge>
              </div>
            </div>

            <FooterLinks />
          </>
        )}

        <div className={`${compact ? "" : "border-t border-muted mt-8 pt-8"} text-center text-muted text-sm space-y-2`}>
          <p>© {new Date().getFullYear()} Longshine Boi-Tech Pvt. Ltd. All rights reserved.</p>
          <p>
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            {" · "}
            <Link to="/return-policy" className="hover:text-primary transition-colors">
              Return Policy
            </Link>
            {" · "}
            <Link to="/contact" className="hover:text-primary transition-colors">
              Contact
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
